import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Table,
  Typography,
  Space,
  Button,
  Progress,
  Tag,
  List,
  Avatar,
  Spin
} from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  DollarOutlined,
  TransactionOutlined,
  RiseOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  WalletOutlined,
  BankOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  LineChartOutlined,
  PieChartOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { Line, Pie } from '@ant-design/charts';

const { Title, Text } = Typography;

const MerchantDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    fetchDashboardOverview();
  }, []);

  const fetchDashboardOverview = async () => {
    try {
      const token = localStorage.getItem('merchant_access_token');
      if (!token) {
        console.error('No merchant token found');
        setLoading(false);
        return;
      }

      const response = await axios.get('/api/merchant/overview', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Dashboard data from API:', response.data);
      setOverview(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard overview:', error);

      // Only use mock data as fallback if API fails
      const mockData = {
        balance: {
          available_balance: 0,
          pending_balance: 0,
          total_earned: 0,
          total_settled: 0
        },
        transaction_stats: {
          total_transactions: 0,
          completed_transactions: 0,
          pending_transactions: 0,
          failed_transactions: 0,
          total_completed_amount: 0,
          avg_transaction_amount: 0
        },
        recent_activity: [],
        payment_method_usage: []
      };

      console.log('Using empty fallback data for new merchant');
      setOverview(mockData);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const numericAmount = Number(amount);
    if (isNaN(numericAmount)) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(numericAmount);
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'green',
      pending: 'orange',
      failed: 'red',
      expired: 'volcano',
      cancelled: 'gray'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      completed: <CheckCircleOutlined />,
      pending: <ClockCircleOutlined />,
      failed: <CloseCircleOutlined />,
      expired: <ClockCircleOutlined />,
      cancelled: <CloseCircleOutlined />
    };
    return icons[status] || <ClockCircleOutlined />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  const balance = overview?.balance || {};
  const transactionStats = overview?.transaction_stats || {};
  const recentActivity = overview?.recent_activity || [];
  const paymentMethodUsage = overview?.payment_method_usage || [];

  // Calculate success rate
  const successRate = transactionStats.total_transactions > 0
    ? ((transactionStats.completed_transactions / transactionStats.total_transactions) * 100).toFixed(1)
    : 0;

  // Prepare charts data with validation
  const activityChartData = (recentActivity || [])
    .filter(item => item && item.date && item.daily_amount)
    .map(item => ({
      date: dayjs(item.date).format('DD/MM'),
      transactions: item.transaction_count || 0,
      amount: parseInt(item.daily_amount) || 0
    }))
    .filter(item => !isNaN(item.amount));

  const paymentMethodChartData = (paymentMethodUsage || [])
    .filter(item => item && item.method_name && item.usage_count > 0)
    .map(item => ({
      type: item.method_name,
      value: parseInt(item.usage_count) || 0,
      amount: parseFloat(item.total_amount) || 0
    }))
    .filter(item => !isNaN(item.value) && item.value > 0);

  // Chart configurations with fallback
  const lineConfig = activityChartData.length > 0 ? {
    data: activityChartData,
    xField: 'date',
    yField: 'amount',
    smooth: true,
    color: '#3b82f6',
    point: {
      size: 5,
      shape: 'diamond',
    },
    tooltip: {
      formatter: (datum) => ({
        name: 'Total Transaksi',
        value: formatCurrency(datum.amount || 0)
      })
    }
  } : {
    data: [{ date: 'No Data', amount: 0 }],
    xField: 'date',
    yField: 'amount',
    color: '#3b82f6',
  };

  const pieConfig = paymentMethodChartData.length > 0 ? {
    data: paymentMethodChartData,
    angleField: 'value',
    colorField: 'type',
    radius: 0.8,
    color: ['#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1', '#13c2c2', '#eb2f96', '#fa541c'],
    interactions: [{ type: 'element-active' }],
    tooltip: {
      formatter: (datum) => ({
        name: datum.type,
        value: `${datum.value} transaksi (${formatCurrency(datum.amount)})`
      })
    },
    legend: {
      position: 'bottom',
      layout: 'horizontal'
    }
  } : null;

  return (
    <div className="p-6">
      <div className="mb-6">
        <Title level={2}>Dashboard Merchant</Title>
        <Text type="secondary">Selamat datang! Kelola bisnis Anda dengan mudah.</Text>
      </div>

      {/* Balance Overview - Updated for Deposit Balance System */}
      <Row gutter={24} className="mb-6">
        <Col xl={6} lg={12} md={12} sm={24} xs={24}>
          <Card>
            <Statistic
              title="Saldo Deposit"
              value={formatCurrency(balance.deposit_balance || 0)}
              prefix={<WalletOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a', fontSize: '18px' }}
            />
            <div className="mt-2">
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <InfoCircleOutlined /> Untuk cash out langsung
              </Text>
            </div>
          </Card>
        </Col>
        <Col xl={6} lg={12} md={12} sm={24} xs={24}>
          <Card>
            <Statistic
              title="Saldo Tersedia"
              value={formatCurrency(balance.available_balance || 0)}
              prefix={<BankOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff', fontSize: '18px' }}
            />
            <div className="mt-2">
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <InfoCircleOutlined /> Dari transaksi settle
              </Text>
            </div>
          </Card>
        </Col>
        <Col xl={6} lg={12} md={12} sm={24} xs={24}>
          <Card>
            <Statistic
              title="Saldo Pending"
              value={formatCurrency(balance.pending_balance || 0)}
              prefix={<HistoryOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14', fontSize: '18px' }}
            />
            <div className="mt-2">
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <InfoCircleOutlined /> Menunggu settlement
              </Text>
            </div>
          </Card>
        </Col>
        <Col xl={6} lg={12} md={12} sm={24} xs={24}>
          <Card>
            <Statistic
              title="Total Semua Saldo"
              value={formatCurrency(
                (parseFloat(balance.deposit_balance) || 0) +
                (parseFloat(balance.available_balance) || 0) +
                (parseFloat(balance.pending_balance) || 0)
              )}
              prefix={<WalletOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1', fontSize: '18px' }}
            />
            <div className="mt-2">
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <InfoCircleOutlined /> Total keseluruhan saldo
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Statistics Cards */}
      <Row gutter={24} className="mb-6">
        <Col xl={6} lg={12} md={12} sm={24} xs={24}>
          <Card>
            <Statistic
              title="Total Transaksi"
              value={transactionStats.total_transactions || 0}
              prefix={<TransactionOutlined />}
              valueStyle={{ color: '#1890ff', fontSize: '18px' }}
            />
            <div className="mt-2">
              <Text type="secondary">
                Bulan ini
              </Text>
            </div>
          </Card>
        </Col>
        <Col xl={6} lg={12} md={12} sm={24} xs={24}>
          <Card>
            <Statistic
              title="Total Nilai Transaksi Berhasil"
              value={formatCurrency(transactionStats.total_completed_amount || 0)}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#722ed1', fontSize: '18px' }}
            />
            <div className="mt-2">
              <Text type="secondary">
                Dari transaksi berhasil
              </Text>
            </div>
          </Card>
        </Col>
        <Col xl={6} lg={12} md={12} sm={24} xs={24}>
          <Card>
            <Statistic
              title="Tingkat Keberhasilan"
              value={successRate}
              suffix="%"
              prefix={<RiseOutlined />}
              valueStyle={{
                color: successRate >= 90 ? '#3f8600' : successRate >= 70 ? '#faad14' : '#f5222d',
                fontSize: '18px'
              }}
            />
            <div className="mt-2">
              <Progress
                percent={successRate}
                showInfo={false}
                strokeColor={successRate >= 90 ? '#52c41a' : successRate >= 70 ? '#faad14' : '#ff4d4f'}
                size="small"
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Transaction Activity Chart */}
      <Row gutter={24} className="mb-6">
        <Col xl={16} lg={24} md={24} sm={24} xs={24}>
          <Card title="Aktivitas Transaksi (30 Hari Terakhir)">
            {activityChartData.length > 0 ? (
              <Line {...lineConfig} height={400} />
            ) : (
              <div className="text-center py-12">
                <Text type="secondary">Belum ada data transaksi</Text>
              </div>
            )}
          </Card>
        </Col>
        <Col xl={8} lg={24} md={24} sm={24} xs={24}>
          <Card title="Metode Pembayaran" extra={
            <Text type="secondary" style={{ fontSize: 12 }}>
              30 hari terakhir
            </Text>
          }>
            {pieConfig ? (
              <Pie {...pieConfig} height={400} />
            ) : (
              <div className="text-center py-12">
                <PieChartOutlined style={{ fontSize: 48, color: '#f0f0f0', marginBottom: 16 }} />
                <div>
                  <Text type="secondary">Belum ada transaksi dalam 30 hari terakhir</Text>
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Transaction Summary & Payment Methods */}
      <Row gutter={24}>
        <Col xl={12} lg={24} md={24} sm={24} xs={24}>
          <Card
            title="Ringkasan Transaksi"
            extra={<Button type="link" onClick={() => navigate('/merchant/transactions')}>Lihat Semua</Button>}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Card size="small" className="text-center">
                  <Statistic
                    title="Berhasil"
                    value={transactionStats.completed_transactions || 0}
                    valueStyle={{ color: '#52c41a' }}
                    prefix={<CheckCircleOutlined />}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" className="text-center">
                  <Statistic
                    title="Pending"
                    value={transactionStats.pending_transactions || 0}
                    valueStyle={{ color: '#faad14' }}
                    prefix={<ClockCircleOutlined />}
                  />
                </Card>
              </Col>
            </Row>
            <Row gutter={16} className="mt-3">
              <Col span={12}>
                <Card size="small" className="text-center">
                  <Statistic
                    title="Gagal"
                    value={transactionStats.failed_transactions || 0}
                    valueStyle={{ color: '#ff4d4f' }}
                    prefix={<CloseCircleOutlined />}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" className="text-center">
                  <Statistic
                    title="Rata-rata"
                    value={formatCurrency(transactionStats.avg_transaction_amount || 0)}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xl={12} lg={24} md={24} sm={24} xs={24}>
          <Card
            title="Metode Pembayaran Populer"
            extra={<Button type="link" onClick={() => navigate('/merchant/payments')}>Kelola</Button>}
          >
            {paymentMethodUsage && paymentMethodUsage.length > 0 ? (
              <List
                dataSource={paymentMethodUsage.slice(0, 5)}
                renderItem={(item, index) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <Avatar style={{
                          backgroundColor: ['#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1'][index % 5]
                        }}>
                          {index + 1}
                        </Avatar>
                      }
                      title={item.method_name || 'Unknown'}
                      description={
                        <Space direction="vertical" size="small">
                          <Space>
                            <Text type="secondary">
                              {item.usage_count || 0} transaksi
                            </Text>
                            <Text strong>
                              {formatCurrency(item.total_amount || 0)}
                            </Text>
                          </Space>
                          {item.usage_count > 0 && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {((item.usage_count / (paymentMethodUsage.reduce((sum, p) => sum + p.usage_count, 0))) * 100).toFixed(1)}% dari total
                            </Text>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <div className="text-center py-8">
                <Text type="secondary">Belum ada data metode pembayaran</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MerchantDashboard;